import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ImageCarouselType } from '../../../models/ContentManagementInterface';
import AssetImage from '../../AssetImage';

type CarouselProps = ImageCarouselType['properties'];
type CarouselItem = CarouselProps['items'][number];

function CropReviewTile({
  item,
  index,
  dropped,
  onToggle,
}: {
  item: CarouselItem;
  index: number;
  dropped: boolean;
  onToggle: () => void;
}) {
  return (
    <Box sx={{ width: 200, position: 'relative', opacity: dropped ? 0.4 : 1 }}>
      <IconButton
        size="small"
        onClick={onToggle}
        aria-label={`Remove card ${index + 1}`}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          bgcolor: 'rgba(255,255,255,0.9)',
          zIndex: 1,
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <AssetImage
        url={item.imageUrl?.url}
        alt={item.heading?.content || `card ${index + 1}`}
        sx={{
          width: '100%',
          height: 130,
          objectFit: 'cover',
          borderRadius: 1,
          border: '1px solid #e0e0e0',
        }}
      />
      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mt: 0.5 }}>
        {item.heading?.content || `Card ${index + 1}`}
      </Typography>
    </Box>
  );
}

/**
 * Shown after a low-confidence screenshot → image-cards split: the user checks
 * each auto-crop and can drop any that are wrong before the cards land.
 */
export default function CropReviewDialog({
  open,
  properties,
  onUse,
  onAdjust,
  onCancel,
}: {
  open: boolean;
  properties: CarouselProps | null;
  onUse: (props: CarouselProps) => void;
  onAdjust: () => void;
  onCancel: () => void;
}) {
  const [dropped, setDropped] = useState<Set<number>>(new Set());
  useEffect(() => setDropped(new Set()), [properties]);

  if (!properties) return null;
  const items = properties.items;
  const kept = items.filter((_, i) => !dropped.has(i));

  const toggle = (i: number) =>
    setDropped((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      aria-labelledby="crop-review-title"
    >
      <DialogTitle id="crop-review-title">Check the automatic crops</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          The screenshot wasn&apos;t a clean match, so these crops may be imprecise. Remove any that
          are wrong — you can drop the original photo onto a card afterwards to replace it.
        </DialogContentText>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {items.map((item, i) => (
            <CropReviewTile
              key={i}
              item={item}
              index={i}
              dropped={dropped.has(i)}
              onToggle={() => toggle(i)}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={onAdjust}>Adjust crops…</Button>
        <Box>
          <Button onClick={onCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => onUse({ ...properties, items: kept })}
            variant="contained"
            disabled={kept.length === 0}
          >
            Use {kept.length} {kept.length === 1 ? 'card' : 'cards'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
